import React, { useState, useEffect, useCallback } from 'react';
import { Card, Statistic, Tag, Spin, Alert, Tooltip, Popover } from 'antd';
import {
  SyncOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  InfoCircleOutlined,
  BankOutlined,
  CalculatorOutlined
} from '@ant-design/icons';
import dollarService from '../../utils/api/dollarService'
import './DollarRateCard.css';

const DollarRateCard = ({ position = 'header', compact = false }) => {
  const [dollarRate, setDollarRate] = useState(null);
  const [banxicoRate, setBanxicoRate] = useState(null);
  const [previousRate, setPreviousRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [rateDate, setRateDate] = useState(null);
  const [variation, setVariation] = useState(0);
  const [variationDirection, setVariationDirection] = useState('equal');
  const PRICE_DIFFERENCE = 1.00;

  const setDefaultValues = useCallback(() => {
    if (!dollarRate) {
      const defaultBanxicoRate = 17.50;
      const defaultOurRate = defaultBanxicoRate - PRICE_DIFFERENCE;
      setBanxicoRate(defaultBanxicoRate);
      setDollarRate(defaultOurRate);
      setLastUpdated(new Date().toLocaleTimeString());
      setError('Usando valores por defecto. No se pudo conectar al servidor.');
    }
  }, [dollarRate, PRICE_DIFFERENCE]);

  const fetchDollarRate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await dollarService.getDollarRate();

      const ourRate = data.banxicoRate - PRICE_DIFFERENCE;

      if (dollarRate !== null && ourRate !== dollarRate) {
        const variationValue = ((ourRate - dollarRate) / dollarRate) * 100;
        setVariation(Math.abs(variationValue).toFixed(2));
        setVariationDirection(ourRate > dollarRate ? 'up' : 'down');
        setPreviousRate(dollarRate);
      } else if (dollarRate === null) {
        setVariation(0);
        setVariationDirection('equal');
      }

      setBanxicoRate(data.banxicoRate);
      setDollarRate(ourRate);
      setRateDate(data.date);
      setLastUpdated(data.lastUpdated);
      setError(null);

      const cacheData = {
        rate: {
          banxicoRate: data.banxicoRate,
          ourRate,
          date: data.date,
          lastUpdated: data.lastUpdated
        },
        timestamp: Date.now()
      };
      localStorage.setItem('dollarRateCache', JSON.stringify(cacheData));

    } catch (error) {
      setError('No se pudo obtener el tipo de cambio de Banxico.');

      const cachedData = localStorage.getItem('dollarRateCache');
      if (cachedData) {
        try {
          const { rate, timestamp } = JSON.parse(cachedData);
          const cacheAge = Date.now() - timestamp;

          if (cacheAge < 24 * 60 * 60 * 1000) {
            setBanxicoRate(rate.banxicoRate);
            setDollarRate(rate.ourRate);
            setRateDate(rate.date);
            setLastUpdated(rate.lastUpdated);
            setError('Mostrando datos almacenados. El servidor no responde.');
          } else {
            setDefaultValues();
          }
        } catch (e) {
          setDefaultValues();
        }
      } else {
        setDefaultValues();
      }
    } finally {
      setLoading(false);
    }
  }, [dollarRate, PRICE_DIFFERENCE, setDefaultValues]);

  useEffect(() => {
    let isMounted = true;
    let intervalId;

    const fetchData = async () => {
      if (isMounted) {
        await fetchDollarRate();
      }
    };

    fetchData();

    intervalId = setInterval(() => {
      if (isMounted) {
        fetchDollarRate();
      }
    }, 2 * 60 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [fetchDollarRate]);

  const getVariationIcon = () => {
    switch (variationDirection) {
      case 'up':
        return <ArrowUpOutlined style={{ color: '#cf1322' }} />;
      case 'down':
        return <ArrowDownOutlined style={{ color: '#3f8600' }} />;
      default:
        return <MinusOutlined style={{ color: '#999' }} />;
    }
  };

  const getVariationColor = () => {
    switch (variationDirection) {
      case 'up':
        return '#cf1322';
      case 'down':
        return '#3f8600';
      default:
        return '#999';
    }
  };

  const rateDetails = (
    <div className="rate-details-popover">
      <div className="rate-detail-item">
        <span className="detail-label">Banxico oficial:</span>
        <span className="detail-value banxico-rate">${banxicoRate?.toFixed(2) || 'N/A'}</span>
      </div>
      <div className="rate-detail-item">
        <span className="detail-label">Fecha Banxico:</span>
        <span className="detail-value">{rateDate || 'N/A'}</span>
      </div>
      <div className="rate-detail-item">
        <span className="detail-label">Fluctuación cambiaria:</span>
        <span className="detail-value discount">- ${PRICE_DIFFERENCE.toFixed(2)}</span>
      </div>
      <div className="rate-detail-item">
        <span className="detail-label">Precio a la compra EPNO:</span>
        <span className="detail-value final-price">${dollarRate?.toFixed(2) || 'N/A'}</span>
      </div>
    </div>
  );

  if (compact) {
    return (
      <Popover content={rateDetails} title="Detalles de Tipo de Cambio" trigger="hover">
        <div className={`dollar-rate-card compact ${position}`}>
          <div className="dollar-rate-content">
            <BankOutlined className="dollar-icon" />
            <div className="dollar-info">
              <div className="dollar-label">Dólar Empresa</div>
              <div className="dollar-value">
                {loading ? (
                  <Spin size="small" />
                ) : error ? (
                  <span className="error-text">Error</span>
                ) : (
                  <>
                    <span className="our-price">${dollarRate?.toFixed(2) || 'N/A'}</span>
                    <small className="original-price">
                      (Banxico: ${banxicoRate?.toFixed(2)})
                    </small>
                  </>
                )}
              </div>
            </div>
            {variation !== 0 && (
              <div className="variation-indicator" style={{ color: getVariationColor() }}>
                {getVariationIcon()}
                <span>{variation}%</span>
              </div>
            )}
            <Tooltip title="Actualizar precio">
              <SyncOutlined
                className="refresh-icon"
                onClick={fetchDollarRate}
                spin={loading}
              />
            </Tooltip>
          </div>
        </div>
      </Popover>
    );
  }

  return (
    <Card
      className={`dollar-rate-card ${position}`}
      size="small"
      title={
        <div className="card-title">
          <BankOutlined />
          <span>Precio a la compra EPNO</span>
          <Tag color="green" style={{ marginLeft: 8, fontSize: 10 }}>
            FIX {rateDate || ''}
          </Tag>
        </div>
      }
      extra={
        <Tooltip title="Actualizar datos">
          <SyncOutlined
            onClick={fetchDollarRate}
            spin={loading}
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
      }
    >
      <div className="dollar-card-content">
        <div className="rate-display">
          <Statistic
            title={
              <div className="statistic-title">
                <span>Precio a la compra EPNO</span>
                <Tag color="green" size="small" style={{ marginLeft: 8 }}>
                  -${PRICE_DIFFERENCE}
                </Tag>
              </div>
            }
            value={dollarRate || 0}
            precision={2}
            prefix="$"
            valueStyle={{
              color: '#52c41a',
              fontSize: '28px',
              fontWeight: 'bold'
            }}
            loading={loading}
          />

          <div className="rate-comparison">
            <div className="comparison-item">
              <span className="comparison-label">
                <BankOutlined style={{ marginRight: 4 }} />
                Banxico FIX:
              </span>
              <span className="comparison-value banxico">
                ${banxicoRate?.toFixed(2) || 'N/A'}
              </span>
            </div>
            {rateDate && (
              <div className="comparison-item">
                <span className="comparison-label">
                  <InfoCircleOutlined style={{ marginRight: 4 }} />
                  Fecha:
                </span>
                <span className="comparison-value date">
                  {rateDate}
                </span>
              </div>
            )}
            <div className="comparison-item">
              <span className="comparison-label">
                <CalculatorOutlined style={{ marginRight: 4 }} />
                Fluctuación cambiaria:
              </span>
              <span className="comparison-value discount">
                - ${PRICE_DIFFERENCE.toFixed(2)}
              </span>
            </div>
          </div>

          {variation !== 0 && previousRate && (
            <div className="variation-container">
              <div className="variation-display" style={{ color: getVariationColor() }}>
                {getVariationIcon()}
                <span className="variation-value">{variation}%</span>
                <span className="variation-text">
                  {variationDirection === 'up' ? 'Subió' : 'Bajó'} desde ${previousRate.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        {error && (
          <Alert
            message="Atención"
            description={error}
            type="warning"
            showIcon
            size="small"
            style={{ marginTop: 10 }}
          />
        )}

        <div className="card-footer">
          <div className="footer-info">
            <InfoCircleOutlined style={{ marginRight: 5, fontSize: 12 }} />
            <span className="info-text">
              {lastUpdated ? `Actualizado: ${lastUpdated}` : 'Cargando...'}
            </span>
          </div>
          <Popover content={rateDetails} title="Detalles de Tipo de Cambio" trigger="hover">
            <Tag color="green" size="small" style={{ cursor: 'pointer' }}>
              <CalculatorOutlined /> Ver cálculo
            </Tag>
          </Popover>
        </div>
      </div>
    </Card>
  );
};

export default DollarRateCard;